/**
 * Retry any async task with backoff.
 * @template T
 * @param {() => Promise<T>} task - Function returning a Promise each attempt.
 * @param {Object} [opts]
 * @param {number} [opts.retries=3]         - Total attempts (including first).
 * @param {number} [opts.timeout=2000]      - Per-attempt timeout (ms).
 * @param {number} [opts.minDelay=200]      - Initial delay before next attempt (ms).
 * @param {number} [opts.maxDelay=5000]     - Max delay cap (ms).
 * @param {number} [opts.factor=2]          - Exponential backoff factor.
 * @param {boolean} [opts.jitter=true]      - Add jitter to delays.
 * @param {(err:any, attempt:number) => boolean|Promise<boolean>} [opts.shouldRetry]
 *        Return false to stop early (e.g., on non-retryable errors).
 * @param {(err:any, attempt:number, nextDelay:number) => void} [opts.onRetry]
 *        Called before waiting to retry.
 * @returns {Promise<T>}
 */
export async function retryAsync(task, opts = {}) {
  const {
    retries = 3,
    timeout = 2000,
    minDelay = 200,
    maxDelay = 5000,
    factor = 2,
    jitter = true,
    shouldRetry,
    onRetry,
  } = opts;

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const withTimeout = (p, ms, label = 'operation') =>
    new Promise((resolve, reject) => {
      const id = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      p.then(
        (v) => {
          clearTimeout(id);
          resolve(v);
        },
        (e) => {
          clearTimeout(id);
          reject(e);
        },
      );
    });

  let delay = minDelay;
  let lastErr;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const res = await withTimeout(Promise.resolve().then(task), timeout, 'retryable task');
      return res; // success
    } catch (err) {
      lastErr = err;

      // If the task threw an explicitly non-retryable error, allow opt-out.
      if (typeof shouldRetry === 'function') {
        const ok = await shouldRetry(err, attempt);
        if (!ok) throw err;
      }

      const isLast = attempt === retries;
      if (isLast) break;

      let nextDelay = delay;
      if (jitter) nextDelay = Math.min(maxDelay, Math.floor(delay * (0.5 + Math.random())));
      if (typeof onRetry === 'function') onRetry(err, attempt, nextDelay);

      await sleep(nextDelay);
      delay = Math.min(maxDelay, Math.floor(delay * factor));
    }
  }

  if (lastErr) {
    throw lastErr;
  }
  throw new Error('retryAsync failed with unknown error');
}

export default {
  install: function (Vue, options) {
    Vue.prototype.$withRetry = retryAsync;
    Vue.$withRetry = retryAsync;
  },
};
